import { marcaDaArea } from '@vestigo/core/area-marca';
import { useEffect } from 'react';

/**
 * A marca da área, aplicada ao DOCUMENTO — favicon, título da aba e `og:image`.
 *
 * ## Por que isto mexe no `<head>` em vez de ser um componente
 *
 * Porque favicon, título da aba e `og:image` não são conteúdo da página: são
 * propriedades do documento, e não há onde desenhá-los. A SPA é servida de um
 * `index.html` estático e igual para todas as áreas — sem isto, a aba de quem
 * estuda no curso de outra pessoa mostra "Área de membros" e o ícone padrão.
 *
 * ## Por que isto saiu de `App.tsx`
 *
 * Porque era aqui que o ícone do site morria, e não havia como medir isso sem
 * montar a aplicação inteira. `marca.favicon` chega do servidor como OBJETO
 * (`{url, srcset, …}`), a linha escrevia `icone.href = marca.favicon`, e o
 * navegador foi pedir `/[object%20Object]` — o mesmo defeito do logo, na outra
 * ponta da mesma resposta, medido em produção em 23/09/2026. Num módulo, a
 * cerca monta um `<head>` de verdade e lê o `href` que saiu.
 *
 * ## Por que o ORIGINAL, e não o degrau derivado
 *
 * Porque `src` é o original e é ele que estes dois consumidores precisam: um
 * favicon WebP não é lido por todo cliente de e-mail que busca o ícone da área,
 * e os agregadores pedem `og:image` com ≥1200px — servir o degrau de 640
 * trocaria um defeito por outro. O argumento inteiro está em `capas-da-area.ts`;
 * o `<img>` do cabeçalho, que é tela e não metadado, opta pelo degrau.
 *
 * ## O que o `og:image` alcança, e o que não alcança
 *
 * Alcança quem renderiza a página. **Não** alcança a prévia de um link colado no
 * WhatsApp ou no LinkedIn: esses robôs não executam JavaScript, e a área do
 * aluno é uma SPA atrás de login — não há HTML público para eles lerem. A ficha
 * da marca no painel diz isso com estas palavras, para ninguém escolher a imagem
 * esperando o contrário.
 */
export function useMarca(marcaCrua: unknown, nome: string | undefined): void {
  /* Desembrulhado aqui, e as DEPENDÊNCIAS do efeito continuam sendo os dois
     endereços — strings. Depender do objeto desembrulhado faria o efeito correr
     a cada desenho: `marcaDaArea` devolve um objeto novo toda vez, e o React
     compara por identidade. O `<head>` seria reescrito sem nada ter mudado. */
  const marca = marcaDaArea(marcaCrua);
  const favicon = marca.favicon?.src ?? null;
  const compartilhamento = marca.compartilhamento?.src ?? null;

  useEffect(() => {
    if (nome) document.title = nome;

    /* Reaproveita a tag quando ela já existe: criar uma nova a cada troca de
       área deixaria o `<head>` crescendo enquanto a pessoa navega. */
    const tag = <T extends HTMLElement>(seletor: string, criar: () => T): T => {
      const achada = document.head.querySelector<T>(seletor);
      if (achada) return achada;
      const nova = criar();
      document.head.appendChild(nova);
      return nova;
    };

    if (favicon) {
      const icone = tag<HTMLLinkElement>('link[rel="icon"]', () => {
        const l = document.createElement('link');
        l.rel = 'icon';
        return l;
      });
      icone.href = favicon;
    }

    if (compartilhamento) {
      const og = tag<HTMLMetaElement>('meta[property="og:image"]', () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:image');
        return m;
      });
      og.content = compartilhamento;
    }

    if (nome) {
      const titulo = tag<HTMLMetaElement>('meta[property="og:title"]', () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:title');
        return m;
      });
      titulo.content = nome;
    }
  }, [favicon, compartilhamento, nome]);
}
